import React, {FC} from "react";
import useSafeState from "../../utils/safe-state";
import {useAxios} from "../../utils/hooks";
import {Breadcrumb, Button, InputNumber, message, Modal, Popconfirm, Popover, Space, Table, Tooltip} from "antd";
import {antdContext} from "../../utils/antdContext";
import FileDownload from "js-file-download";
import {Trans} from "@lingui/macro";
import {
    DeleteOutlined,
    EditTwoTone,
    FileImageTwoTone,
    PlusOutlined,
    RightOutlined,
    SnippetsOutlined
} from "@ant-design/icons";
import {Column} from "../../models/column";
import DtoTable from "../../components/dto-table/dto-table";
import StockModel from "../../models/dto/stock-model";
import InventoryModel from "../../models/dto/inventory-model";
import {CheckCircleOutlined, CloseCircleOutlined} from "@ant-design/icons/lib";
import {openNotification} from "../../utils/openNotification";

const InventoryScreen : FC = () => {

    const [trigger, setTrigger] = useSafeState(false);
    const [visible, setVisible] = useSafeState(false);
    const [actQty, setActQty] = useSafeState(0);
    const [maxQty, setMaxQty] = useSafeState<any>();
    const [actProduct, setActProduct] = useSafeState<any>();
    const [isModalVisible, setIsModalVisible] = useSafeState(false);
    const [deliveryNoteData, setDeliveryNoteData] = useSafeState<Array<any>>([]);

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL  + "");
    const config = {headers: {'Content-Type': 'application/json'}};

    const handleApprove = (id: any) => {
        if(axiosInstance.current != null){
            axiosInstance.current.put("/resource/inventory/approve?id="+id)
                .then(() => {
                    message.success(antdContext('Sikeresen jóváhagyva!'));
                    setTrigger(false);
                    setTrigger(!trigger);
                }).catch(function (error :any) {
                    openNotification('error', error)
                    setTrigger(false);
                    setTrigger(!trigger);
                    console.log(error);
            });
        }
    }

    const handleReject = (id: any) => {
        if(axiosInstance.current != null){
            axiosInstance.current.put("/resource/inventory/reject?id="+id)
                .then(() => {
                    message.success(antdContext('Sikeresen elutasítva!'));
                    setTrigger(false);
                    setTrigger(!trigger);
                }).catch(function (error :any) {
                    openNotification('error', error)
                    setTrigger(false);
                    setTrigger(!trigger);
                    console.log(error);
            });
        }
    }

    const hide = () => {
        setVisible(false);
    };

    const handleVisibleChange = (newVisible: boolean) => {
        setVisible(newVisible);
    };

    const showModal = (record: any) => {
        setIsModalVisible(true);
        setActQty(0);
        setMaxQty(record.freeQuantity > 0 ? record.freeQuantity : 0);
        setActProduct(
            {
                product: record.product,
                lotNumber: record.lotNumber,
                quantity: 0,
                slot: record.slot,
            }
        );
    };

    const handleOk = () => {
        if(actQty > 0 && actQty <= maxQty && isAllowed()){
            setIsModalVisible(false);
            actProduct.quantity = actQty;

            setDeliveryNoteData([...deliveryNoteData,
                {
                    key: (deliveryNoteData.length + 1).toString(),
                    product: actProduct.product,
                    lotNumber: actProduct.lotNumber,
                    quantity: actQty,
                    slot: actProduct.slot,
                }
            ]);
            setVisible(true);
        }
    };

    const isAllowed = (): boolean => {

        let sumQty = 0;
        for (let i = 0; i < deliveryNoteData.length; i++) {
            if(
                deliveryNoteData[i].product.code == actProduct.product.code &&
                deliveryNoteData[i].lotNumber == actProduct.lotNumber
            ){
                sumQty += deliveryNoteData[i].quantity;
            }
        }
        console.log(sumQty, actQty, maxQty);
        return sumQty + actQty <= maxQty;
    }

    const handleCancel = () => {
        setIsModalVisible(false);
    };


    const action = (text: string, record: any) => {

        return  <>
            <Tooltip placement="top" title={<Trans>Approve</Trans>}>
                {record.status == 'CREATED'
                    ?
                        <Popconfirm title={<Trans>Are you sure to approve this inventory?</Trans>}
                                    onConfirm={() => handleApprove(record.id)}
                                    okText={<Trans>Yes</Trans>}
                                    cancelText={<Trans>No</Trans>}>
                            <CheckCircleOutlined className={"ActionButtons"} style={{color: "green"}}/>
                        </Popconfirm>
                    :
                    <CheckCircleOutlined className={"ActionButtons"} style={{color: "gray"}}/>
                }
            </Tooltip>
            <Tooltip placement="top" title={<Trans>Reject</Trans>}>

                {record.status == 'CREATED'
                ?
                    <Popconfirm title={<Trans>Are you sure to reject this inventory?</Trans>}
                                onConfirm={() => handleReject(record.id)}
                                okText={<Trans>Yes</Trans>}
                                cancelText={<Trans>No</Trans>}>
                        <CloseCircleOutlined className={"ActionButtons"} style={{color: "#ff4d4f"}}/>
                    </Popconfirm>
                :
                    <CloseCircleOutlined className={"ActionButtons"} style={{color: "gray"}}/>
                }
            </Tooltip>
        </>
    }


    const actions = {
        width: 100,
        render: (text: string, record: any) => action(text, record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const qtyText = <Trans>Quantity</Trans>

    const delivNoteTitleText = <h2><Trans>Delivery Note</Trans></h2>


    const columns : Array<Column> = new Array();

    columns.push({key:'product',dataIndex:["product", "code"], width: 150, direction: "asc", fixed: "left", sorter:false, title:<Trans>Product</Trans>});
    columns.push({key:'lotNumber', dataIndex:["lotNumber"], width: 150, sorter:false,title:<Trans>Lot Number</Trans>});
    columns.push({key:'quantity',dataIndex:["quantity"], width: 150, sorter:false,title:<Trans>Quantity</Trans>});
    columns.push({key:'slot', dataIndex:["slot"], width: 150, sorter:false,title:<Trans>Slot</Trans>,
        render: (text, row) =>
            ({
                children: <div>
                    {row["slot"]["rack"]}:
                    {row["slot"]["row"]}:
                    {row["slot"]["col"]}
                </div>
            }),
    });

    columns.push(
        {
            key: 'action',
            sorter: false,
            width: 50,
            title: <></>,
            render: (_, record) => (
                <Space size="middle">
                    <DeleteOutlined className={"ActionButtons"} style={{color: "#ff4d4f"}} onClick={() => {
                        const newData = deliveryNoteData.filter(item => item.key !== record.key);
                        setDeliveryNoteData(newData);
                    }
                    }/>
                </Space>
            ),
        }
    );

    return  <>
        <Breadcrumb style={{ marginBottom: 10 }}>
            <Breadcrumb.Item><Trans id={'Homepage'}/></Breadcrumb.Item>
            <Breadcrumb.Item><Trans id={'Stocks'}></Trans></Breadcrumb.Item>
        </Breadcrumb>
        <DtoTable
            model={InventoryModel}
            // tableHeader={
            // <>
            //     <Tooltip placement="top" title={<Trans>Approve All </Trans>}>
            //         <Popconfirm title={<Trans>Are you sure to approve this inventory?</Trans>}
            //                     // onConfirm={() => handleDelete()}
            //                     okText={<Trans>Yes</Trans>}
            //                     cancelText={<Trans>No</Trans>}>
            //             <Button style={{float: "left", marginRight:10}}><CheckCircleOutlined /><span><Trans>Approve All Created</Trans></span></Button>
            //         </Popconfirm>
            //     </Tooltip>
            //     <Tooltip placement="top" title={<Trans>Reject All </Trans>}>
            //         <Popconfirm title={<Trans>Are you sure to reject this inventory?</Trans>}
            //             // onConfirm={() => handleDelete()}
            //                     okText={<Trans>Yes</Trans>}
            //                     cancelText={<Trans>No</Trans>}>
            //             <Button style={{float: "left", marginRight:10}}><CloseCircleOutlined /><span><Trans>Reject All Created</Trans></span></Button>
            //         </Popconfirm>
            //     </Tooltip>
            // </>
            // }
            allowRefreshButton={true}
            action={actions}
            trigger={trigger}
            pageSize={100}
            scroll={{ x: 1300, y: "60vh" }}
        />
        <Modal title={qtyText} visible={isModalVisible} onOk={handleOk}
               onCancel={handleCancel} width={230}>
            <InputNumber value={actQty}
                         onInput={
                             (value) => {
                                 value <= maxQty ? setActQty(Number(value)) : setActQty(0);
                             }
                         }
                         onStep={
                             (value) => {
                                 value <= maxQty ? setActQty(value) : setActQty(0);
                             }
                         }

                         min={0} max={maxQty} style={{width: "100%"}}/>

            <span style={{color: "red", fontStyle: "italic"}}><Trans>Available max. quantity: {maxQty}</Trans></span>
        </Modal>
    </>
}
export default InventoryScreen;
